import {
  useDeleteLayoutFileMutation,
  useDeleteBuildingPackageFileMutation,
  useDeleteBlobMutation,
} from '@consigli/hooks';
import { useCallback } from 'react';

import { successToast, errorToast } from '@/components/toast';
import { ServiceType } from '@/utils/types';

// This hook expects a showDeleteModal function to be passed in, which should
// open a modal and return a Promise that resolves to true if the user confirms.
export const useDeleteDocument = (
  projectId: number,
  serviceId: string,
  serviceType: ServiceType,
  showDeleteModal: (item: unknown) => Promise<boolean>,
) => {
  const [deleteLayoutFile] = useDeleteLayoutFileMutation();
  const [deleteBuildingPackageFile] = useDeleteBuildingPackageFileMutation();
  const [deleteBlob] = useDeleteBlobMutation();

  return useCallback(
    async (item: unknown) => {
      // If it's a Blob, handle delete confirmation and logic here
      if (item && typeof item === 'object' && 'name' in item && 'id' in item) {
        const blobItem = item as { name: string; id: string };
        const confirmed = await showDeleteModal(item);
        if (confirmed) {
          try {
            await deleteBlob({
              projectId,
              packageId: serviceId,
              blobId: blobItem.id,
            }).unwrap();
            successToast({ message: `File "${blobItem.name}" deleted successfully` });
          } catch (error) {
            console.error('Failed to delete blob:', error);
            errorToast({ message: `Failed to delete file "${blobItem.name}"` });
          }
        }
      }
      // If it's a File (layout file or building package file), use the appropriate delete mutation
      else if (item && typeof item === 'object' && 'id' in item && 'filename' in item) {
        const fileItem = item as { filename: string; id: string };
        const confirmed = await showDeleteModal(item);
        if (confirmed) {
          try {
            if (serviceType === ServiceType.LAYOUT_OPTIMIZATION) {
              await deleteLayoutFile({
                projectId,
                layoutId: serviceId,
                fileId: fileItem.id,
              }).unwrap();
              successToast({ message: `Layout file "${fileItem.filename}" deleted successfully` });
            } else if (serviceType === ServiceType.BUILDING_PACKAGE) {
              await deleteBuildingPackageFile({
                projectId,
                buildingPackageId: serviceId,
                fileId: fileItem.id,
              }).unwrap();
              successToast({
                message: `Building package file "${fileItem.filename}" deleted successfully`,
              });
            }
          } catch (error) {
            console.error('Failed to delete file:', error);
            errorToast({ message: `Failed to delete file "${fileItem.filename}"` });
          }
        }
      }
    },
    [
      deleteLayoutFile,
      deleteBuildingPackageFile,
      deleteBlob,
      projectId,
      serviceId,
      serviceType,
      showDeleteModal,
    ],
  );
};
